import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useFinanceMeta, createStatement } from '@/hooks/useFinance';
import { ArrowLeft, Loader2, ChevronDown } from 'lucide-react';
import DateInput from '@/components/DateInput';
import { isoToDisplay } from '@/lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Big underline-style display field.
// Renders a Select that LOOKS like a giant headline with a bottom underline.
// Accepts options as either string[] (`['A','B']`) or `{value,label}[]`.
const HeadlineSelect = ({ testid, value, onValueChange, options, placeholder = 'Choose', disabled }) => {
  const normalised = options.map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        data-testid={testid}
        className="h-auto w-full bg-transparent border-0 border-b-2 border-ink/15 rounded-none px-0 pb-2 pt-0 font-display text-5xl font-bold text-ink tracking-tight focus:ring-0 focus:border-primary hover:border-ink/40 transition-colors flex items-center justify-between gap-3 [&>span]:!line-clamp-none [&>span]:truncate [&>svg]:hidden"
      >
        <SelectValue placeholder={placeholder} />
        <span className="inline-flex shrink-0">
          <ChevronDown className="w-7 h-7 text-ink/70" />
        </span>
      </SelectTrigger>
      <SelectContent>
        {normalised.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const AddStatement = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const meta = useFinanceMeta();

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(params.get('clientId') || '');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Expense');
  const [subCategory, setSubCategory] = useState('');
  const [bank, setBank] = useState('');
  const [remark, setRemark] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/crm/client-options`).then((r) => setClients(r.data.clients || []))
      .catch(() => setClients([]));
  }, []);

  const subCategories = useMemo(() => {
    return (meta?.sub_categories?.[category]) || [];
  }, [meta, category]);

  // Reset sub-category when category changes
  useEffect(() => { setSubCategory(''); }, [category]);

  const selectedClient = clients.find((c) => c.id === clientId);

  const handleSave = async () => {
    if (!selectedClient) { toast.error('Choose a company first'); return; }
    const cleaned = String(amount).replace(/[^0-9.]/g, '');
    const numeric = Number(cleaned);
    if (!cleaned || !Number.isFinite(numeric) || numeric <= 0) {
      toast.error('Enter a valid amount, e.g. 150000');
      return;
    }
    if (!subCategory) { toast.error('Choose a sub-category'); return; }
    if (category === 'Income' && !bank) { toast.error('Choose a bank for income'); return; }

    setSaving(true);
    try {
      const payload = {
        client_id: selectedClient.id,
        company_name: selectedClient.company?.company_name || '',
        amount: numeric,
        category,
        sub_category: subCategory,
        date,
      };
      if (category === 'Income') payload.bank = bank;
      if (category === 'Expense') payload.remark = remark;
      await createStatement(payload);

      const thisMonth = date.slice(0, 7) === new Date().toISOString().slice(0, 7);
      toast.success(
        `${category} of ₹${numeric.toLocaleString('en-IN')} saved for ${payload.company_name}`,
        {
          description: thisMonth
            ? `Dated ${isoToDisplay(date)}`
            : `Dated ${isoToDisplay(date)} — outside this month, use the "All Time" filter to see it`,
          duration: thisMonth ? 4000 : 8000,
        },
      );
      navigate(clientId ? `/finance/company/${clientId}` : '/finance');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save the statement — please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="add-statement-page">
      {/* Header: title + subtitle left, back arrow */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div className="flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-ink/5 text-ink/70"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-display text-4xl font-bold text-ink tracking-tight">Add Statement</h1>
            <p className="text-ink/60 mt-1">Record an income, expense, or pending entry</p>
          </div>
        </div>
      </div>

      {/* Body — big underlined headline-style fields */}
      <div className="space-y-12">
        {/* Company Name — big underlined display-style dropdown (matches Amount/Category) */}
        <div>
          <label className="text-sm font-medium text-ink/70 mb-2 block">Company Name</label>
          <HeadlineSelect
            testid="select-client"
            value={clientId}
            onValueChange={setClientId}
            options={clients.map((c) => ({ value: c.id, label: c.company?.company_name || '' }))}
            placeholder={clients.length === 0 ? 'Loading companies…' : 'Choose'}
          />
        </div>

        {/* Amount + Category — side-by-side big underlined inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <label className="text-sm font-medium text-ink/70 mb-2 block">Amount</label>
            <div className="flex items-baseline border-b-2 border-ink/15 focus-within:border-primary transition-colors pb-2">
              <span className="font-display text-5xl font-bold text-ink mr-2">₹</span>
              <input
                data-testid="input-amount"
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
                placeholder="0"
                className="flex-1 bg-transparent outline-none font-display text-5xl font-bold text-ink tracking-tight placeholder:text-ink/20 min-w-0"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-ink/70 mb-2 block">Category</label>
            <HeadlineSelect
              testid="select-category"
              value={category}
              onValueChange={setCategory}
              options={meta?.categories || ['Income', 'Expense', 'Pending']}
            />
          </div>
        </div>

        {/* Sub-Category — big underlined dropdown, full width */}
        <div>
          <label className="text-sm font-medium text-ink/70 mb-2 block">Sub-Category</label>
          <HeadlineSelect
            testid="select-sub-category"
            value={subCategory}
            onValueChange={setSubCategory}
            options={subCategories}
            placeholder="Choose"
          />
        </div>

        {/* Date + Remark/Bank — side-by-side per wireframe */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <label className="text-sm font-medium text-ink/70 mb-2 block">Date</label>
            <DateInput
              testid="input-date"
              value={date}
              onChange={setDate}
              className="w-full h-auto bg-transparent border-0 border-b-2 rounded-none border-ink/15 focus-visible:ring-0 focus:border-primary pb-2 pl-0 text-xl font-medium text-ink transition-colors"
            />
          </div>

          {category === 'Income' && (
            <div data-testid="bank-field">
              <label className="text-sm font-medium text-ink/70 mb-2 block">Bank</label>
              <HeadlineSelect
                testid="select-bank"
                value={bank}
                onValueChange={setBank}
                options={meta?.banks || []}
                placeholder="Choose"
              />
            </div>
          )}

          {category === 'Expense' && (
            <div data-testid="remark-field">
              <label className="text-sm font-medium text-ink/70 mb-2 block">Remark</label>
              <input
                data-testid="input-remark"
                type="text"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="e.g. Gpay - Sanjoe"
                className="w-full bg-transparent border-b-2 border-ink/15 focus:border-primary focus:outline-none pb-2 text-xl font-medium text-ink placeholder:text-ink/30 transition-colors"
              />
            </div>
          )}
        </div>

        {/* Confirm — big primary CTA pill, centered */}
        <div className="pt-8 flex justify-center">
          <button
            data-testid="confirm-btn"
            onClick={handleSave}
            disabled={saving}
            className="h-14 px-16 rounded-full bg-primary text-white font-display text-xl font-semibold tracking-tight shadow-lg shadow-primary/20 hover:bg-primary-hover transition-all disabled:opacity-60 inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-5 h-5 animate-spin" />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStatement;
